import { useEffect, useRef } from 'react';

const regions = [
  { label: 'Asia', icon: 'ri-map-pin-2-line', desc: 'Hong Kong headquarters with deep network across Southeast Asia, East Asia, and South Asia.', x: '80%', y: '44%' },
  { label: 'Europe', icon: 'ri-map-pin-2-line', desc: 'Strong relationships with European buyers and commodity processors.', x: '44%', y: '24%' },
  { label: 'Americas', icon: 'ri-map-pin-2-line', desc: 'North and South American producer connections for agricultural and mineral commodities.', x: '22%', y: '38%' },
  { label: 'Africa', icon: 'ri-map-pin-2-line', desc: 'Access to mineral-rich African producers for ferrous, non-ferrous, and bulk minerals.', x: '48%', y: '54%' },
  { label: 'Middle East', icon: 'ri-map-pin-2-line', desc: 'Strategic supply chain links to Middle Eastern buyers and fertilizer markets.', x: '67%', y: '46%' },
];

const strengths = [
  { icon: 'ri-focus-2-line', title: 'Strategic Location', desc: 'Hong Kong provides access to global financial markets, efficient logistics infrastructure, and strong regulatory transparency.' },
  { icon: 'ri-pie-chart-2-line', title: 'Diversified Portfolio', desc: 'A balanced portfolio across metals, minerals, fertilizers, and agricultural commodities ensures resilience and market adaptability.' },
  { icon: 'ri-brain-line', title: 'Market Expertise', desc: 'Deep industry experience across mining, agriculture, logistics, and commodity trading enables informed decision-making.' },
  { icon: 'ri-shield-star-line', title: 'Risk Awareness', desc: 'Understanding commodity market dynamics allows structured and reliable trade execution for all partners.' },
  { icon: 'ri-shake-hands-line', title: 'Partnership Approach', desc: 'Focus on long-term relationships built on transparency, reliability, and consistent performance delivery.' },
];

export default function GlobalNetwork() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-item').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="network" className="bg-[#F5F5F5] py-28" ref={sectionRef}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">

        {/* Header */}
        <div className="reveal-item opacity-0 translate-y-8 transition-all duration-700 mb-4 text-center">
          <span className="inline-block border border-[#2A2A2A]/20 rounded-full px-6 py-2 text-[#2A2A2A] text-xs uppercase tracking-[0.3em]">
            Worldwide Presence
          </span>
        </div>
        <div className="reveal-item opacity-0 translate-y-8 transition-all duration-700 text-center mb-6">
          <h2 className="text-4xl md:text-5xl font-playfair text-[#2A2A2A]">Global Network</h2>
        </div>
        <div className="reveal-item opacity-0 translate-y-8 transition-all duration-700 text-center mb-16">
          <p className="text-[#6A6A6A] text-lg max-w-2xl mx-auto leading-relaxed">
            We maintain relationships with producers, processors, distributors, and end-users worldwide — aligning supply capabilities with customer demand through market insight and operational expertise.
          </p>
        </div>

        {/* World Map Visual */}
        <div className="reveal-item opacity-0 translate-y-8 transition-all duration-700 mb-20 relative rounded-3xl overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/45e8e4ff9af1456cb33805c024d3d04e.jpg"
            alt="Bulk Ore Global Network"
            className="w-full h-72 md:h-96 object-cover object-center rounded-3xl"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D1B2A]/30 to-[#0D1B2A]/60 rounded-3xl" />

          {/* Region Labels */}
          {regions.map((region) => (
            <div
              key={region.label}
              className="absolute hidden lg:flex items-center gap-1.5 group cursor-pointer"
              style={{ left: region.x, top: region.y, transform: 'translate(-50%, -50%)' }}
            >
              <div className="relative">
                <div className="w-3 h-3 rounded-full bg-[#888888] animate-pulse" />
                <div className="absolute inset-0 w-3 h-3 rounded-full bg-[#888888]/40 scale-150 animate-ping" />
              </div>
              <span className="bg-[#2A2A2A]/80 text-[#CCCCCC] text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap backdrop-blur-sm">
                {region.label}
              </span>
            </div>
          ))}
        </div>

        {/* Regions Grid - mobile */}
        <div className="reveal-item opacity-0 translate-y-8 transition-all duration-700 lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4 mb-16">
          {regions.map((region, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 border border-[#E0E0E0]">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2.5 h-2.5 rounded-full bg-[#888888]" />
                <h4 className="font-semibold text-[#2A2A2A] text-sm">{region.label}</h4>
              </div>
              <p className="text-[#6A6A6A] text-xs leading-relaxed">{region.desc}</p>
            </div>
          ))}
        </div>

        {/* Competitive Strengths */}
        <div className="reveal-item opacity-0 translate-y-8 transition-all duration-700 mb-8">
          <h3 className="text-3xl font-playfair text-[#2A2A2A] mb-2">Competitive Strengths</h3>
          <div className="h-px w-16 bg-[#888888]" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {strengths.map((s, i) => (
            <div key={i} className="reveal-item opacity-0 translate-y-8 transition-all duration-700 bg-white rounded-2xl p-6 border border-[#E0E0E0] hover:border-[#888888] transition-all hover:-translate-y-1 group">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#3A3A3A] mb-4 group-hover:bg-[#4A4A4A] transition-colors duration-300">
                <i className={`${s.icon} text-white text-lg`} />
              </div>
              <h4 className="font-semibold text-[#2A2A2A] text-sm mb-2">{s.title}</h4>
              <p className="text-[#6A6A6A] text-xs leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
